import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { buildFirstApprovedFieldsDotNotation, appendReportAuditEntry } from './reportAuditHelpers';
import { classifyReportError } from './reportErrorMessages';

/**
 * Fire generatePatientReport (fire-and-forget) with client-side error handling
 * for network failures. Shared by the clinician path (after a successful
 * client status write) and the Support path (CF owns status writes).
 */
function invokeGeneratePatientReport({
  clinicianId,
  patientId,
  reportType,
  renderCfName,
  patientDocRef,
  clientOwnsErrorStatusWrite,
}) {
  const generatePatientReport = httpsCallable(functions, 'generatePatientReport', {
    timeout: 600000,
  });

  generatePatientReport({
    clinicianId,
    patientId,
    reportType,
    renderCfName: renderCfName || 'renderPatientReportTemplate',
  }).then((result) => {
    console.log('Patient report generated successfully:', result.data);
  }).catch((error) => {
    console.error('Error in patient report generation:', error);

    if (error.code === 'deadline-exceeded') {
      console.log('Client-side timeout occurred, but function continues processing in background');
      return;
    }

    // Support / deferred-status path: client often cannot write dataModel.
    // Leave error handling to the CF / stuck-processing self-heal.
    if (!clientOwnsErrorStatusWrite) {
      return;
    }

    getDoc(patientDocRef).then((snap) => {
      const mr = snap.data()?.dataModel?.['6_manageReports'];
      if (mr?.prReportApproved === true) {
        console.log('Report already approved — suppressing error, restoring complete status');
        updateDoc(patientDocRef, {
          'dataModel.6_manageReports.prReportProcessingStatus': 'complete',
          'dataModel.6_manageReports.prReportProcessingError': null,
        }).catch((e) => console.error('Failed to restore complete status:', e));
      } else {
        console.error('Setting patient report status to error');
        const serverReason = mr?.prReportProcessingError;
        updateDoc(patientDocRef, {
          'dataModel.6_manageReports.prReportProcessingStatus': 'error',
          'dataModel.6_manageReports.prReportProcessingError': serverReason || classifyReportError(error),
        }).catch((e) => console.error('Failed to set error status:', e));
      }
    }).catch((e) => console.error('Failed to read doc for error handling:', e));
  });
}

function isPermissionDeniedError(error) {
  const code = error?.code || '';
  const message = String(error?.message || error || '');
  return (
    code === 'permission-denied' ||
    code === 'firestore/permission-denied' ||
    message.toLowerCase().includes('permission')
  );
}

/**
 * Auto-generate and finalize patient report for report_logic_version = 2 patients.
 *
 * Structurally mirrors TriggerCoverPage: a transactional guard prevents
 * duplicate invocations, and a single Cloud Function (generatePatientReport)
 * is called fire-and-forget so this function returns immediately with
 * { status: 'processing' }.
 *
 * The generate CF now chains the render step server-side (when renderCfName
 * is provided) so the browser is no longer responsible for making a second
 * CF call.  This eliminates the class of bugs where a tab teardown or
 * client-side timeout between generate and render left the report stuck at
 * 'processing' permanently.
 *
 * Support / unassigned Org Admin: cannot write dataModel, so the client
 * transaction fails with permission-denied. In that case we still invoke
 * generatePatientReport; the CF bootstraps processing status, approvedAt,
 * and renderIntent via Admin SDK.
 *
 * Key design decisions:
 *
 *   prReportApprovedAt is written at TRIGGER TIME (before the CF runs), not
 *   at completion.  This anchors the staleness comparison in
 *   rerenderPatientReportIfStale so that any section re-approved during the
 *   CF run has lastReviewedAt > prReportApprovedAt -> correctly detected as
 *   stale -> triggers a retry.
 *
 *   prReportApproved (boolean) is NOT touched here.  It flips true only
 *   when the render step writes 'complete' on the server.
 *
 *   prReportRenderIntent persists the trigger parameters so the backend
 *   reconciliation sweep can replay the render without re-deriving
 *   reportType or renderCfName from patient data.
 *
 * @param {Object} params
 * @param {string} params.clinicianId
 * @param {string} params.patientId
 * @param {string} params.reportType
 * @param {string} [params.renderCfName]
 * @returns {Promise<Object>} { success, data/error }
 */
export const TriggerPatientReportGeneration = async ({ clinicianId, patientId, reportType, renderCfName }) => {
  console.log('Starting auto patient report generation for version 2 patient:', { clinicianId, patientId, reportType, renderCfName });

  const user = auth.currentUser;
  if (!user || !user.uid) {
    return { success: false, error: 'User not authenticated' };
  }

  if (!clinicianId || !patientId || !reportType) {
    console.error('Missing required parameters:', { clinicianId, patientId, reportType });
    return { success: false, error: 'Missing required parameters' };
  }

  const patientDocRef = doc(db, 'patients', patientId);
  const resolvedRenderCfName = renderCfName || 'renderPatientReportTemplate';

  let alreadyProcessing = false;
  let manageReports = null;
  let deferStatusToServer = false;

  try {
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(patientDocRef);
      manageReports = snap.data()?.dataModel?.['6_manageReports'];

      if (manageReports?.prReportProcessingStatus === 'processing') {
        alreadyProcessing = true;
        return;
      }

      transaction.update(patientDocRef, {
        'dataModel.6_manageReports.prReportProcessingStatus': 'processing',
        'dataModel.6_manageReports.prReportProcessingStartedAt': serverTimestamp(),
        'dataModel.6_manageReports.prReportApprovedAt': serverTimestamp(),
        'dataModel.6_manageReports.prReportProcessingError': null,
        'dataModel.6_manageReports.prReportRenderIntent': {
          reportType,
          renderCfName: resolvedRenderCfName,
          clinicianId,
        },
        ...buildFirstApprovedFieldsDotNotation('pr', manageReports),
      });
    }, { maxAttempts: 10 });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      // Support / unassigned Org Admin — CF will bootstrap metadata.
      console.log(
        'Client cannot write PR processing status (permission-denied) — deferring to generatePatientReport CF'
      );
      deferStatusToServer = true;
      try {
        const snap = await getDoc(patientDocRef);
        manageReports = snap.data()?.dataModel?.['6_manageReports'];
        if (manageReports?.prReportProcessingStatus === 'processing') {
          alreadyProcessing = true;
        }
      } catch (readErr) {
        console.error('Failed to read patient doc after permission-denied:', readErr);
      }
    } else {
      console.error('Error in TriggerPatientReportGeneration transaction:', error);
      return { success: false, error };
    }
  }

  if (alreadyProcessing) {
    console.log('Skipping patient report generation — already processing');
    return { success: true, data: { status: 'already-processing' } };
  }

  appendReportAuditEntry(patientId, {
    module: 'pr',
    eventType: 'approved',
    isFirstOccurrence: !manageReports?.prReportFirstApprovedAt,
  });

  invokeGeneratePatientReport({
    clinicianId,
    patientId,
    reportType,
    renderCfName: resolvedRenderCfName,
    patientDocRef,
    clientOwnsErrorStatusWrite: !deferStatusToServer,
  });

  return { success: true, data: { status: 'processing' } };
};
